/**
 * Service Facilities Tab
 *
 * List view with CRUD operations for managing service facility locations.
 * Mirrors the Billing Identities tab pattern in BillingSettingsPage.
 */

import { useState } from 'react';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';
import { useSnackbar } from 'notistack';

import { useServiceFacilities, useServiceFacilityOperations } from '../store';
import { extractServiceFacilityApiError } from '../utils';

import { EditServiceFacilityModal } from './EditServiceFacilityModal';
import { SetDefaultFacilityModal } from './SetDefaultFacilityModal';

export function ServiceFacilitiesTab() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { facilities, refetch } = useServiceFacilities();
  const { remove, setDefault, setDefaultLoading } = useServiceFacilityOperations();

  const [editingFacility, setEditingFacility] = useState<ServiceFacilityResponse2 | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [settingDefaultFacility, setSettingDefaultFacility] = useState<ServiceFacilityResponse2 | null>(null);
  const [deletingFacility, setDeletingFacility] = useState<ServiceFacilityResponse2 | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleSaved = () => {
    refetch();
  };

  const handleSetDefaultConfirm = async () => {
    if (!settingDefaultFacility) return;

    try {
      await setDefault({ serviceFacilityId: settingDefaultFacility.service_facility_id }).unwrap();
      enqueueSnackbar(
        `${settingDefaultFacility.display_name} is now your default service facility`,
        { variant: 'success' },
      );
      setSettingDefaultFacility(null);
      refetch();
    } catch (error) {
      const errorMessage = extractServiceFacilityApiError(
        error,
        'Failed to set default service facility.',
      );
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFacility) return;

    setDeleteLoading(true);
    try {
      await remove({ serviceFacilityId: deletingFacility.service_facility_id }).unwrap();
      enqueueSnackbar(`${deletingFacility.display_name} deleted`, { variant: 'success' });
      setDeletingFacility(null);
      refetch();
    } catch (error) {
      const errorMessage = extractServiceFacilityApiError(
        error,
        'Failed to delete service facility.',
      );
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Manage Service Facilities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Service facilities represent where care is rendered (CMS-1500 Box 32)
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreatingNew(true)}
          >
            Add Facility
          </Button>
        </Box>

        {facilities.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {facilities.map(facility => (
              <Box
                key={facility.service_facility_id}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: theme.palette.grey[200],
                  borderRadius: 2,
                  bgcolor: theme.palette.background.paper,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  '&:hover': {
                    borderColor: theme.palette.grey[300],
                  },
                }}
              >
                {/* Facility Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                      {facility.display_name}
                    </Typography>
                    {facility.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {facility.address?.line1}
                      {facility.address?.city && `, ${facility.address.city}`}
                      {facility.address?.state && `, ${facility.address.state}`}
                      {facility.address?.zip && ` ${facility.address.zip}`}
                    </Typography>
                    {facility.facility_npi && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          NPI: {facility.facility_npi}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip
                    title={facility.is_default ? 'Default service facility' : 'Set as default'}
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => !facility.is_default && setSettingDefaultFacility(facility)}
                        disabled={setDefaultLoading}
                        sx={{
                          color: facility.is_default ? 'success.main' : 'inherit',
                          '&.Mui-disabled': {
                            color: facility.is_default ? 'success.main' : undefined,
                          },
                          cursor: facility.is_default ? 'default' : 'pointer',
                          pointerEvents: facility.is_default ? 'none' : 'auto',
                        }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit facility" arrow>
                    <IconButton size="small" onClick={() => setEditingFacility(facility)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete facility" arrow>
                    <IconButton size="small" onClick={() => setDeletingFacility(facility)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              Add your first service facility
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }} component="p">
              Service facilities tell insurance companies where care was rendered. You&apos;ll need:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
              <Typography component="li" variant="body2">
                Facility name
              </Typography>
              <Typography component="li" variant="body2">
                Facility address
              </Typography>
              <Typography component="li" variant="body2">
                Facility NPI (optional, if the location has its own)
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreatingNew(true)}
              sx={{ mt: 1 }}
            >
              Add Service Facility
            </Button>
          </Alert>
        )}
      </CardContent>

      {/* Create/Edit Modal */}
      {(creatingNew || editingFacility) && (
        <EditServiceFacilityModal
          open={creatingNew || !!editingFacility}
          onClose={() => {
            setCreatingNew(false);
            setEditingFacility(null);
          }}
          facility={editingFacility}
          onSaved={handleSaved}
        />
      )}

      {/* Set Default Modal */}
      <SetDefaultFacilityModal
        open={!!settingDefaultFacility}
        facility={settingDefaultFacility}
        onClose={() => setSettingDefaultFacility(null)}
        onConfirm={handleSetDefaultConfirm}
        isLoading={setDefaultLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingFacility}
        onClose={() => setDeletingFacility(null)}
        fullWidth
       maxWidth={"sm"}>
        <DialogTitle>Delete Service Facility?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete{' '}
            <strong>{deletingFacility?.display_name}</strong>? This action cannot be undone.
          </Typography>
          {deletingFacility?.is_default && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is your default service facility. Deleting it will require you to set a new
              default.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFacility(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
